import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  MachineScheduleViewModel,
  ProductionOrderDocumentInfo,
  ProductionOrderDocuments,
  ProductionOrderPrintedLabelInfo,
  LabelType
} from 'chronos-core-client';
import { filter, switchMap, tap } from 'rxjs/operators';
import {
  ContainersDsService,
  DocumentsDsService,
  MachineScheduleDsService,
  OutputUnitDsService,
  ProducedMaterialDsService,
  ProductionOrderDsService
} from '@app/core/data-services';

@Injectable()
export class DocumentsService {
  private productionOrderIdSubject = new BehaviorSubject<number>(undefined);
  public productionOrderId$ = this.productionOrderIdSubject.asObservable().pipe(filter((id) => !!id));

  constructor(
    private machineScheduleDsService: MachineScheduleDsService,
    private documentsDsService: DocumentsDsService,
    private outputUnitDsService: OutputUnitDsService,
    private containersDsService: ContainersDsService,
    private producedMaterialDsService: ProducedMaterialDsService,
    private productionOrderDsService: ProductionOrderDsService
  ) {}

  public getAllDocumentsForOrder(): Observable<ProductionOrderDocuments> {
    return this.productionOrderId$.pipe(switchMap((orderId) => this.documentsDsService.getDocumentList(orderId)));
  }

  public getAllProductionOrders(): Observable<MachineScheduleViewModel[]> {
    return this.machineScheduleDsService.showAllMachineScheduleList();
  }

  public setProductionOrderId(productionOrderId: number): void {
    this.productionOrderIdSubject.next(productionOrderId);
  }

  public getProductionOrderInformation(): Observable<ProductionOrderDocumentInfo> {
    return this.productionOrderId$.pipe(switchMap((orderId) => this.documentsDsService.getProductionOrderInfo(orderId)));
  }

  public saveEditedRemarkInfo(productionOrderId: number, documentInfo: string): Observable<void> {
    return this.documentsDsService.saveRemark(productionOrderId, documentInfo);
  }

  public getPrintedLabelListData(): Observable<ProductionOrderPrintedLabelInfo> {
    return this.productionOrderId$.pipe(
      switchMap((productionOrderId) => this.documentsDsService.getProductionOrderLabelInfo(productionOrderId))
    );
  }

  public refreshSelectedOrder(): void {
    this.productionOrderIdSubject.next(this.productionOrderIdSubject.getValue());
  }

  public saveQuantityPerOuterAndCopies(labelType: LabelType, quantityPerOuter: number, numberOfCopies: number): Observable<void> {
    const productionOrderId = this.productionOrderIdSubject.getValue();
    return this.outputUnitDsService.saveQuantityPerOuterAndCopies(productionOrderId, labelType, quantityPerOuter, numberOfCopies);
  }

  public printContainerLabel(containerId: number, isReprint: boolean): Observable<void> {
    return this.containersDsService.printContainerLabel(containerId, isReprint);
  }

  public markAsDisplayed(documentId: number): Observable<void> {
    const productionOrderId = this.productionOrderIdSubject.getValue();
    return this.documentsDsService.markAsDisplayed(productionOrderId, documentId);
  }

  public searchOrders(
    showOpen?: boolean,
    showFinished?: boolean,
    showEnded?: boolean,
    showActive?: boolean,
    externalProductionOrderIdFilter?: string
  ) {
    return this.productionOrderDsService.searchOrders(showOpen, showFinished, showEnded, showActive, externalProductionOrderIdFilter);
  }
}
